import { Link } from 'react-router-dom';
import Alias from 'components/controls/alias';
import Clipboard from 'blob_types/blobhub/compute/workflow/controls/common/clipboard';
import AccountTypes from 'context/accountTypes';
import Navigation from 'context/nav';
import Utils from 'context/utils';

import './userIdentity.css';

/**
 * UserIdentity — the one way to render a principal.
 *
 * One format for every principal: **glyph · name · alias if it has one · copy**. The copy button
 * always puts the ID on the clipboard, whatever is displayed — which is what makes it safe to show a
 * name rather than an id. A service account has no alias, so it reads glyph · name · copy; before
 * this it rendered a short monospace id, which was the only handle it had and cost a column of space
 * in every grid to say something nobody reads. The type glyph comes from the account-type registry,
 * so a future kind of principal appears here without touching this file.
 *
 * Before this component the four sites that render a user each reached for `alias` directly, which
 * printed the literal string "undefined" beside a link to /p/undefined for anything an account
 * authored.
 *
 * The pill needs `name`, not just `alias`. `Utils.displayName` falls back to the alias when there is
 * no name — a human whose profile carries only an alias, which the profile form permits — and in that
 * position the alias IS the name. Repeating it as a pill would print it twice, and `controls/alias`
 * calls out that exact case as the one place its pill must not be used.
 *
 * `hideAccountId` suppresses the copy affordance, leaving the alias pill untouched — narrower than
 * `showIdentifier`, which drops both. The account's own profile header is the one place that opts in,
 * since the id is reachable there through Settings → Profile.
 *
 * `strong` is the name's weight. Bold where the principal is the SUBJECT — a profile header, a member
 * row — and not where it is a value among values: a grid cell or a property rail, where bolding one
 * field shouts over the ones beside it. Defaults to bold so no existing surface changes; the debugger
 * and the revisions list opt out -- in both, the author is when-and-by-whom beside the thing itself.
 */
export default function UserIdentity({ user, fallback, link = true, showIdentifier = true,
                                       hideAccountId = false, strong = true }) {
  const name = Utils.displayName(user, fallback);
  if ( !name ) {
    return null;
  }

  const Glyph = AccountTypes.of(user).glyph;
  const target = user ? (user.alias || user.id) : null;
  // Always the id, never what is displayed: a name is ambiguous and an alias is mutable, and the
  // reason anybody reaches for this button is to paste the thing an API will accept.
  const identifier = ( user && user.id ) || fallback || null;
  const label = (link && target)
    ? <Link to={Navigation.userProfilePath(target)}>{name}</Link>
    : <span>{name}</span>;
  // The class, not the tag, is what CSS targets — the tag varies with `strong` and the layout rules
  // (grid truncation especially) must not care which one it got.
  const Name = strong ? 'strong' : 'span';

  return (
    <span className="bh-user-identity">
      <Glyph/>
      &nbsp;<Name className="bh-user-identity-name" title={name}>{label}</Name>
      {showIdentifier && user && user.name && user.alias &&
        <>&nbsp;&nbsp;<Alias value={user.alias}/></>
      }
      {showIdentifier && !hideAccountId && identifier &&
        <>&nbsp;&nbsp;<Clipboard text={identifier}/></>
      }
    </span>
  );
}
